import { Popover, Portal, useDisclosure } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ContactLengthPeriodPopoverSchema,
  IContactLengthPeriodPopoverSchema,
  IQuoteRequestSchema,
  IQuoteRespSchema,
  NewQuoteTypeEnum,
  QuoteAmendmentVersionEnum,
  QuoteStartDateSourceEnum,
  QuoteStatusEnum,
  QuoteTypeEnum,
} from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { calculateDateDifference, toDateShort } from '@monetize/utils/core';
import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { format as formatDate } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import { startOfDay } from 'date-fns/startOfDay';
import { useEffect, useId, useMemo, useState } from 'react';
import FocusLock from 'react-focus-lock';
import {
  Controller,
  FormProvider,
  UseFormSetValue,
  useForm,
} from 'react-hook-form';
import { handleApiErrorToast } from '../../api/axios';
import {
  doUpdateDelayedBilling,
  useGetContractById,
} from '../../api/cpqService';
import { enqueueForQuote } from '../../api/quoteWriteChain';
import {
  DELAYED_BILLING_QUOTE_START_SOURCE,
  QUOTE_CUSTOM_CONTRACT_LENGTHS,
  QUOTE_NOT_ACTIONABLE_STATUSES,
  QUOTE_START_DATE_SOURCE_OPTIONS,
} from '../../constants/quotes';
import { useExperienceConfiguration, useQuoteSettings } from '../../hooks';
import { getCanConfigureDelayedBilling } from '../../routes/Quotes/Quote/components/quoteOffering/quoteOfferingUtils';
import { logger } from '../../services/logger';
import {
  MBox,
  MButton,
  MCustomNumberInput,
  MDivider,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MInputElement,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
  MVStack,
  RadioStyleButtonGroup,
} from '../Monetize';
import { DatePicker } from '../Monetize/DatePicker/DatePicker';
import { MTertiaryArrowButton } from '../Monetize/MTertiaryArrowButton';
import { FOCUS_WITHOUT_SCROLLING } from '../Monetize/Overlays';
import { QuoteAnchorBillingDay } from './AnchorBillingDay';

interface IQuoteContractLengthPeriodPopoverProps {
  isCustomContractLength: boolean;
  isNew: boolean;
  isAmendment: boolean;
  isRenewal: boolean;
  quote: IQuoteRespSchema;
  setValue: UseFormSetValue<IQuoteRequestSchema>;
  handleSubmitButton: () => void;
  isReadOnly: boolean;
  renderTrigger?: (props: {
    isOpen: boolean;
    isDisabled: boolean;
    contractLengthFormattedVal: string;
    contractPeriodFormattedVal: string;
    handleOnOpen?: () => void;
  }) => React.ReactNode;
  setQuote: (quote: IQuoteRespSchema) => void;
}

const monthlyContractOptions = QUOTE_CUSTOM_CONTRACT_LENGTHS.map((value) => ({
  label: `${value}`,
  value: `${value}`,
}));

const getContractStartDateTooltipHint = (
  useDelayedBilling: boolean,
  allowQuoteLevelOverrideOfStartDateSource: boolean,
  canEditDelayedBillingStartDate: boolean,
  startDateSource?: QuoteStartDateSourceEnum | null,
) => {
  if (!useDelayedBilling) {
    return { tooltip: '', hint: '' };
  }

  if (!allowQuoteLevelOverrideOfStartDateSource) {
    switch (startDateSource) {
      case QuoteStartDateSourceEnum.MANUAL:
        return {
          tooltip:
            'Starting the contract once its manually processed can move the start date to a later time, which will shift the entire contract. The new dates will be reflected in the contract period and billing schedule.',
          hint: `Contract Starts once it's manually processed.`,
        };
      case QuoteStartDateSourceEnum.AT_TIME_OF_SIGNING:
        return {
          tooltip:
            'Starting the contract once its signed can move the start date to a later time, which will shift the entire contract. The new dates will be reflected in the contract period and billing schedule.',
          hint: 'Contract Starts once signed.',
        };
      case QuoteStartDateSourceEnum.ORIGINAL_CONTRACT_START:
        return {
          tooltip: '',
          hint: 'Contract Starts once signed.',
        };
      default:
        return { tooltip: '', hint: '' };
    }
  }

  if (canEditDelayedBillingStartDate) {
    switch (startDateSource) {
      case QuoteStartDateSourceEnum.MANUAL:
        return {
          tooltip: '',
          hint: `Contract Starts once it's manually processed.`,
        };
      case QuoteStartDateSourceEnum.AT_TIME_OF_SIGNING:
        return {
          tooltip: '',
          hint: 'Contract Starts once signed.',
        };
      case QuoteStartDateSourceEnum.ORIGINAL_CONTRACT_START:
        return {
          tooltip: '',
          hint: 'Contract Starts once signed.',
        };
      default:
        return { tooltip: '', hint: '' };
    }
  }

  switch (startDateSource) {
    case QuoteStartDateSourceEnum.MANUAL:
    case QuoteStartDateSourceEnum.AT_TIME_OF_SIGNING:
      return {
        tooltip:
          'This selection determines when the contract begins. Starting the contract once its signed or manually processed can move the start date to a later time, which will shift the entire contract. The new dates will be reflected in the contract period and billing schedule.',
        hint: '',
      };
    case QuoteStartDateSourceEnum.ORIGINAL_CONTRACT_START:
    default:
      return { tooltip: '', hint: '' };
  }
};

export const QuoteContractLengthPeriodPopover = ({
  isCustomContractLength,
  isNew,
  quote,
  setValue: setQuoteFromValue,
  isAmendment,
  isRenewal,
  handleSubmitButton,
  isReadOnly,
  renderTrigger,
  setQuote,
}: IQuoteContractLengthPeriodPopoverProps) => {
  const { enableBillingAnchorAdjustment } = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );
  // Used to track if user modified end date, in which case we don't auto-update it when start date changes
  const [modifiedContractEndDate, setModifiedContractEndDate] = useState(false);

  // For early renewal quotes, fetch the prior contract to get date constraints
  const isEarlyRenewal =
    isRenewal && quote.newQuoteType === NewQuoteTypeEnum.EARLY_RENEWAL;
  const { data: priorContract, isLoading: isPriorContractLoading } =
    useGetContractById(quote.previousContractId || '', {
      enabled: isEarlyRenewal && !!quote.previousContractId,
    });

  const { onOpen, onClose, open } = useDisclosure();
  const isQuoteAccepted = quote.status === QuoteStatusEnum.ACCEPTED;
  const {
    quoteSettings: {
      allowQuoteLevelOverrideOfStartDateSource,
      pauseAtAccepted,
    } = {},
  } = useQuoteSettings();
  const [isLoading, setIsLoading] = useState(false);
  // Months and days share the "Contract Length" field, whose control id the
  // days input would otherwise take too. zag finds its input by id, so the
  // days value would sync into the months input (the first match).
  const daysInputId = useId();
  const daysInputIds = useMemo(() => ({ input: daysInputId }), [daysInputId]);

  // Calculate contractEndDate if not present (from contractStartDate + contractLength)
  const calculatedEndDate = useMemo(() => {
    if (quote.contractEndDate) {
      return quote.contractEndDate;
    }
    if (quote.contractStartDate && quote.contractLength) {
      // Calculate end date: startDate + contractLength months - 1 day
      const endDate = addDays(
        addMonths(
          startOfDay(parseISO(quote.contractStartDate)),
          quote.contractLength,
        ),
        -1,
      );
      return formatDate(endDate, 'yyyy-MM-dd');
    }
    return null;
  }, [quote.contractStartDate, quote.contractLength, quote.contractEndDate]);

  // Calculate lengthInMonths and lengthInDays from quote dates
  const defaultDateDifference =
    quote.contractStartDate && calculatedEndDate
      ? calculateDateDifference(quote.contractStartDate, calculatedEndDate)
      : { months: 0, days: 0 };

  const methods = useForm<IContactLengthPeriodPopoverSchema>({
    resolver: zodResolver(ContactLengthPeriodPopoverSchema),
    mode: 'onChange',
    defaultValues: {
      contractStartDate: quote.contractStartDate,
      contractEndDate: calculatedEndDate,
      lengthInMonths: defaultDateDifference.months,
      lengthInDays: defaultDateDifference.days,
      startDateSource: quote.startDateSource,
      billingAnchor: {
        day: quote.billingAnchor?.day ?? null,
        month: quote.billingAnchor?.month ?? null,
      },
    },
  });

  const {
    formState: { isDirty, isValid, errors },
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
  } = methods;

  const watchContractStartDate = watch('contractStartDate') || '';
  const watchContractEndDate = watch('contractEndDate') || '';
  const watchStartDateSource = watch('startDateSource');
  const watchBillingAnchor = watch('billingAnchor');
  // Raw value to allow null and 0 without changing the actual data
  const _watchLengthInMonths = watch('lengthInMonths');
  const watchLengthInMonths = _watchLengthInMonths || 0;
  const watchLengthInDays = watch('lengthInDays') || 0;

  const isQuoteNotActionable = QUOTE_NOT_ACTIONABLE_STATUSES.has(quote.status);
  const isDisabled = isQuoteNotActionable;

  const handleOnOpen = () => {
    onOpen();
  };

  useEffect(() => {
    if (quote.contractLength) {
      const dateDifference =
        quote.contractStartDate && calculatedEndDate
          ? calculateDateDifference(quote.contractStartDate, calculatedEndDate)
          : { months: 0, days: 0 };

      reset({
        contractStartDate: quote.contractStartDate,
        contractEndDate: calculatedEndDate,
        lengthInMonths: dateDifference.months,
        lengthInDays: Math.max(0, dateDifference.days),
        startDateSource: quote.startDateSource,
        billingAnchor: {
          day: quote.billingAnchor?.day ?? null,
          month: quote.billingAnchor?.month ?? null,
        },
      });
    }
  }, [isCustomContractLength, quote, reset, open, calculatedEndDate]);

  const onChangeContractLength = (value: number | null) => {
    if (value === null) {
      setValue('lengthInMonths', value);
      return;
    }
    // Reset contract end date modification flag
    setModifiedContractEndDate(false);

    // Value is always in months now
    const contractLengthMonths = value;

    // Validate the input
    if (contractLengthMonths === 0 || contractLengthMonths > 120) {
      return; // Invalid input, do nothing
    }

    // Update lengthInMonths and calculate contractEndDate
    const currentStartDate = getValues('contractStartDate');

    if (currentStartDate && contractLengthMonths > 0) {
      setValue('lengthInMonths', contractLengthMonths, {
        shouldDirty: true,
      });

      // Calculate new end date: startDate + months + days - 1 day
      const newEndDate = addDays(
        addMonths(startOfDay(parseISO(currentStartDate)), contractLengthMonths),
        -1,
      );

      setValue('contractEndDate', formatDate(newEndDate, 'yyyy-MM-dd'), {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('lengthInDays', 0, {
        shouldDirty: true,
      });
    }
  };

  const onChangeContractEndDate = (newEndDate: string | null) => {
    if (!newEndDate) {
      return;
    }

    setModifiedContractEndDate(true);

    const currentStartDate = getValues('contractStartDate');

    if (currentStartDate && newEndDate) {
      // Calculate the difference between start and end dates
      const difference = calculateDateDifference(currentStartDate, newEndDate);

      // Update the form values
      setValue('contractEndDate', newEndDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('lengthInMonths', difference.months, {
        shouldDirty: true,
      });
      setValue('lengthInDays', Math.max(0, difference.days), {
        shouldDirty: true,
      });
    }
  };

  const onChangeContractStartDate = (newStartDate: string | null) => {
    if (!newStartDate) {
      return;
    }

    let currentEndDate = getValues('contractEndDate');
    const currentLengthInMonths = getValues('lengthInMonths');

    setValue('contractStartDate', newStartDate, {
      shouldDirty: true,
      shouldValidate: true,
    });

    // Calculated end date mode: recalculate end date based on start date + length
    if (
      !modifiedContractEndDate &&
      newStartDate &&
      currentLengthInMonths !== undefined &&
      currentLengthInMonths !== null
    ) {
      const newEndDate = addDays(
        addMonths(startOfDay(parseISO(newStartDate)), currentLengthInMonths),
        -1,
      );

      setValue('contractEndDate', formatDate(newEndDate, 'yyyy-MM-dd'), {
        shouldDirty: true,
        shouldValidate: true,
      });

      currentEndDate = formatDate(newEndDate, 'yyyy-MM-dd');
    }

    // Custom end date mode: recalculate length based on start and end dates
    if (currentEndDate && newStartDate) {
      const difference = calculateDateDifference(newStartDate, currentEndDate);

      setValue('lengthInMonths', difference.months, {
        shouldDirty: true,
      });
      setValue('lengthInDays', difference.days, {
        shouldDirty: true,
      });
    }
  };

  // Validation: when isCustomContractLength is false (yearly only mode),
  // contract must be whole years (months % 12 === 0 && days === 0)
  const [contractLengthError, setContractLengthError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!isCustomContractLength) {
      // Yearly only mode - validate whole years
      if (watchLengthInMonths % 12 !== 0 || watchLengthInDays !== 0) {
        setContractLengthError(
          'Contract length must be in whole years for yearly contracts',
        );
      } else {
        setContractLengthError(null);
      }
    } else {
      // Custom mode - no validation needed
      setContractLengthError(null);
    }
  }, [isCustomContractLength, watchLengthInMonths, watchLengthInDays]);

  const willDateChange =
    watchStartDateSource &&
    DELAYED_BILLING_QUOTE_START_SOURCE.has(watchStartDateSource) &&
    !QUOTE_NOT_ACTIONABLE_STATUSES.has(quote.status) &&
    isNew &&
    pauseAtAccepted;

  const canConfigureDelayedBilling = useMemo(
    () => getCanConfigureDelayedBilling(quote),
    [quote],
  );

  const isDisabledQuoteUpdate =
    (isAmendment && quote?.amendmentVersion === QuoteAmendmentVersionEnum.v1) ||
    isReadOnly;

  // Use the humanized date difference if available
  const dateDifference =
    quote.contractStartDate && calculatedEndDate
      ? calculateDateDifference(quote.contractStartDate, calculatedEndDate)
      : null;

  const contractLengthFormattedVal = dateDifference?.humanized || '';

  const contractPeriodFormattedVal =
    quote?.contractStartDate && calculatedEndDate
      ? `${toDateShort(quote?.contractStartDate || null)} - ${toDateShort(
          calculatedEndDate || null,
        )}`
      : '';

  const canEditDelayedBillingStartDate =
    !isRenewal &&
    !isAmendment &&
    isQuoteAccepted &&
    isNew &&
    watchStartDateSource &&
    [QuoteStartDateSourceEnum.MANUAL].includes(watchStartDateSource);

  const handleUpdateDelayedBilling = async () => {
    if (!watchContractStartDate) {
      return;
    }

    try {
      setIsLoading(true);
      const newQuote = await enqueueForQuote(quote.id, () =>
        doUpdateDelayedBilling(quote.id, watchContractStartDate),
      );
      setQuote(newQuote);
      onClose();
    } catch (err) {
      handleApiErrorToast(err);
    }
    setIsLoading(false);
  };

  const onSubmitForm = async () => {
    if (canEditDelayedBillingStartDate) {
      handleUpdateDelayedBilling();
    } else {
      onClose();

      setQuoteFromValue(
        'contractLength',
        watchLengthInMonths + (watchLengthInDays > 0 ? 1 : 0),
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
      setQuoteFromValue('contractStartDate', watchContractStartDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setQuoteFromValue('contractEndDate', watchContractEndDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setQuoteFromValue('startDateSource', watchStartDateSource, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setQuoteFromValue('billingAnchor', watchBillingAnchor, {
        shouldDirty: true,
        shouldValidate: true,
      });
      handleSubmitButton();
    }
  };

  const hasActiveChanges =
    !isValid ||
    !isDirty ||
    !!contractLengthError ||
    _watchLengthInMonths === null;

  return (
    <Popover.Root
      lazyMount
      unmountOnExit
      open={open}
      onOpenChange={(details) => {
        if (details.open) {
          handleOnOpen();
        } else {
          onClose();
        }
      }}
      restoreFocus
      size="lg"
      positioning={{
        strategy: 'fixed',
        placement: 'bottom-end',
        offset: { mainAxis: 0, crossAxis: 0 },
      }}
      closeOnInteractOutside={hasActiveChanges}
    >
      <MFlex minH={8} align="center" mr={-3}>
        <Popover.Trigger asChild>
          <MBox position="relative">
            {renderTrigger &&
              renderTrigger({
                isOpen: open,
                isDisabled,
                contractLengthFormattedVal,
                contractPeriodFormattedVal,
                handleOnOpen,
              })}

            {!renderTrigger && (
              <MTertiaryArrowButton
                isOpen={open}
                setIsOpen={(val) => (val ? handleOnOpen() : onClose())}
                minH="40px"
              >
                {/* MText children render with their own color (fg.primary)
                    instead of inheriting the tertiary button's indigo, so
                    the period/length values default to near-black. Color
                    them explicitly so the trigger reads as clickable. */}
                <MBox textAlign="right">
                  <MText color="tIndigo.base">
                    {contractPeriodFormattedVal}
                  </MText>
                  <MText fontSize="xs" color="tIndigo.base">
                    {contractLengthFormattedVal}
                  </MText>
                </MBox>
              </MTertiaryArrowButton>
            )}
          </MBox>
        </Popover.Trigger>
      </MFlex>

      <Portal>
        <Popover.Positioner>
          <Popover.Content
            data-testid="custom-contractLength-popover-content"
            w="380px"
          >
            <Popover.Body>
              <FocusLock focusOptions={FOCUS_WITHOUT_SCROLLING}>
                <form
                  onSubmit={handleSubmit(onSubmitForm, (error) =>
                    logger.error(error),
                  )}
                >
                  <FormProvider {...methods} data-testid="rate-form">
                    <MVStack gap={2} alignItems={'flex-start'}>
                      {/* If quote is processed, we only allow to update start date */}
                      <MGrid gap={2} templateColumns="repeat(2, 1fr)" w="full">
                        <MGridItem>
                          <MFormField
                            error={errors?.contractStartDate}
                            label={
                              willDateChange
                                ? `Estimated Start Date`
                                : 'Start Date'
                            }
                            {...getContractStartDateTooltipHint(
                              !isAmendment && !isRenewal && !!pauseAtAccepted,
                              !!allowQuoteLevelOverrideOfStartDateSource,
                              !!canEditDelayedBillingStartDate,
                              watchStartDateSource,
                            )}
                          >
                            <Controller
                              name="contractStartDate"
                              control={control}
                              render={({
                                field: { value, onChange, ...rest },
                              }) =>
                                (isRenewal && !isEarlyRenewal) ||
                                (isRenewal &&
                                  isEarlyRenewal &&
                                  (isPriorContractLoading || !priorContract)) ||
                                isAmendment ? (
                                  <MText>
                                    {toDateShort(
                                      quote?.contractStartDate || null,
                                    )}
                                  </MText>
                                ) : (
                                  <DatePicker
                                    {...rest}
                                    value={value}
                                    btnText={toDateShort(
                                      value || '',
                                      'userTimezone',
                                    )}
                                    onChange={(startDate) => {
                                      onChangeContractStartDate(startDate);
                                    }}
                                    isDisabled={!quote}
                                    isReadOnly={
                                      isDisabledQuoteUpdate &&
                                      !canEditDelayedBillingStartDate
                                    }
                                    minDate={
                                      isEarlyRenewal && priorContract
                                        ? parseISO(priorContract.startDate)
                                        : undefined
                                    }
                                    maxDate={
                                      isEarlyRenewal && priorContract
                                        ? parseISO(priorContract.endDate)
                                        : undefined
                                    }
                                  />
                                )
                              }
                            />
                          </MFormField>
                        </MGridItem>
                        <MGridItem>
                          <MFormField
                            error={errors?.contractEndDate}
                            justifyContent={'flex-end'}
                            label={
                              willDateChange ? `Estimated End Date` : 'End Date'
                            }
                          >
                            <Controller
                              name="contractEndDate"
                              control={control}
                              render={({
                                field: { value, onChange, ...rest },
                              }) => (
                                <DatePicker
                                  key={watchContractEndDate}
                                  {...rest}
                                  value={value}
                                  minDate={
                                    watchContractStartDate
                                      ? parseISO(watchContractStartDate)
                                      : undefined
                                  }
                                  btnText={toDateShort(
                                    value || '',
                                    'userTimezone',
                                  )}
                                  onChange={(endDate) => {
                                    onChangeContractEndDate(endDate);
                                  }}
                                  isDisabled={!quote}
                                  isReadOnly={
                                    isDisabledQuoteUpdate &&
                                    !canEditDelayedBillingStartDate
                                  }
                                />
                              )}
                            />
                          </MFormField>
                        </MGridItem>
                      </MGrid>

                      {contractLengthError && (
                        <MText color="tRed.base" fontSize="sm" mt={1}>
                          {contractLengthError}
                        </MText>
                      )}

                      {allowQuoteLevelOverrideOfStartDateSource &&
                        pauseAtAccepted &&
                        quote.type !== QuoteTypeEnum.RENEWAL && (
                          <>
                            <MFormField
                              error={errors?.startDateSource}
                              onClick={(ev) => ev.stopPropagation()}
                              w="auto"
                            >
                              <Controller
                                name="startDateSource"
                                control={control}
                                render={({
                                  field: { value, onChange, ...rest },
                                }) => (
                                  <MRadioGroup
                                    onChange={(val) => onChange(val)}
                                    value={value || ''}
                                    rowGap={2}
                                    isDisabled={
                                      !canConfigureDelayedBilling ||
                                      isDisabledQuoteUpdate
                                    }
                                  >
                                    <MStack direction="column" gap={0}>
                                      {QUOTE_START_DATE_SOURCE_OPTIONS.map(
                                        ({ value: optionValue, title }) => (
                                          <MRadio
                                            value={optionValue}
                                            key={optionValue}
                                            mt="2"
                                          >
                                            {title}
                                          </MRadio>
                                        ),
                                      )}
                                    </MStack>
                                  </MRadioGroup>
                                )}
                              />
                            </MFormField>

                            {!canConfigureDelayedBilling && (
                              <MText color="tRed.base">
                                Edit the offerings to ensure they all begin on
                                the contract start anniversary.
                              </MText>
                            )}
                          </>
                        )}

                      <MFormField isRequired label="Contract Length" mt={4}>
                        {isDisabledQuoteUpdate ? (
                          <MText>{contractLengthFormattedVal}</MText>
                        ) : (
                          <MGrid gap={2} templateColumns="repeat(2, 1fr)">
                            <MGridItem>
                              <MCustomNumberInput
                                inputMode="numeric"
                                // We want to allow empty and 0
                                value={_watchLengthInMonths ?? ''}
                                onChange={(valueAsString, valueAsNumber) => {
                                  onChangeContractLength(
                                    valueAsString === '' ? null : valueAsNumber,
                                  );
                                }}
                                RightElement={
                                  <MInputElement placement="end">
                                    <MText mr="6" color="tBlack.300">
                                      months
                                    </MText>
                                  </MInputElement>
                                }
                              />
                            </MGridItem>
                            <MGridItem>
                              <MCustomNumberInput
                                ids={daysInputIds}
                                inputMode="numeric"
                                value={watchLengthInDays}
                                isDisabled
                                RightElement={
                                  <MInputElement placement="end">
                                    <MText mr="6" color="tBlack.300">
                                      days
                                    </MText>
                                  </MInputElement>
                                }
                              />
                            </MGridItem>
                          </MGrid>
                        )}
                      </MFormField>

                      {!isDisabledQuoteUpdate && (
                        <RadioStyleButtonGroup
                          name="contractLength"
                          options={monthlyContractOptions}
                          value={`${watchLengthInMonths}` || undefined}
                          defaultValue={`${watchLengthInMonths}` || undefined}
                          onChange={(value: string) => {
                            const numericValue = Number(value);
                            onChangeContractLength(numericValue);
                          }}
                        />
                      )}

                      {enableBillingAnchorAdjustment && (
                        <>
                          <MDivider mt={2} />
                          <QuoteAnchorBillingDay
                            isDisabled={
                              isDisabled ||
                              (isDisabledQuoteUpdate &&
                                !canEditDelayedBillingStartDate)
                            }
                            isAmendmentOrRenewal={isAmendment || isRenewal}
                          />
                        </>
                      )}
                      <MDivider />

                      <MFlex justifyContent="space-between" w="100%">
                        <MButton
                          onClick={onClose}
                          variant="cancel"
                          minWidth={'80px'}
                        >
                          Cancel
                        </MButton>
                        <MButton
                          type="submit"
                          minWidth={'80px'}
                          isDisabled={hasActiveChanges}
                          isLoading={isLoading}
                        >
                          Save
                        </MButton>
                      </MFlex>
                    </MVStack>
                  </FormProvider>
                </form>
              </FocusLock>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};
